import React, { useEffect, useState, useCallback } from 'react';
import { FONT, colors, radii } from '../../theme';
import { S } from './dashboardStyles';
import { Shield, ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react';
import { api } from '../../services/api';
import { identity, tofu } from '../../services/tunnel';
import { secureBackend, isEvictable, secureKeyNames } from '../../services/secureStore.mjs';
import { shortFingerprint } from '../../services/chatTrust.mjs';
import { displayName } from '../../services/displayName';

/**
 * What the encryption is actually doing, in a form a person can check.
 *
 * The protection has been real since the chat started pinning identity keys —
 * but real and checkable are different things, and until this screen a person
 * had no way to see whose keys their messages were going to, no way to tell a
 * confirmed device from one that was merely first, and no way to undo a
 * confirmation given by mistake.
 *
 * Deliberately does NOT fetch key bundles: asking for one spends a one-time
 * prekey of the person being looked at, and a screen that quietly drains
 * somebody else's keys every time it is opened would be its own bug. Labels
 * come from the free device listing; the keys come from what this device has
 * pinned.
 */
export default function SecurityCard({ userId }) {
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // A refresh with no visible outcome is indistinguishable from a dead button.
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const friends = await api.getFriends();
      // Which peer devices this one actually holds a session with. Without it
      // the screen could only count keys, and a device carried over from
      // before key checking existed looked exactly like a brand new one — so
      // it said "sending is stopped" about conversations that were working.
      const sessionKeys = new Set(
        secureKeyNames()
          .filter(k => k.includes(':session:'))
          .map(k => k.split(':session:')[1]),
      );
      const rows = await Promise.all((friends || []).map(async (f) => {
        // Free: says which devices exist, spends nothing.
        let devices = [];
        try { devices = (await api.listDevices(f.id))?.devices || []; } catch (e) { /* offline */ }
        const pinned = tofu.keysFor(f.id) || [];
        const withSession = devices.map(d => ({
          ...d, hasSession: sessionKeys.has(`${f.id}:${d.deviceId}`),
        }));
        return { user: f, devices: withSession, pinned };
      }));
      setContacts(rows);
      setRefreshedAt(new Date());
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить — нет связи с сервером');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const forget = (peerId, name) => {
    tofu.forget(peerId);
    setNote(`Доверие к ${name} сброшено — ключи будут запомнены заново при следующем сообщении`);
    setTimeout(() => setNote(''), 6000);
    load();
  };

  const ownFingerprint = (() => {
    try {
      const pub = identity.publicKey();
      let s = '';
      for (const b of pub) s += String.fromCharCode(b);
      return shortFingerprint(btoa(s));
    } catch (e) { return '—'; }
  })();

  return (
    <div style={{ ...S.settingsCard, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Shield size={18} color={colors.gray900} />
        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: colors.gray900 }}>
          Безопасность
        </div>
        <button onClick={load} disabled={busy}
          style={{
            ...S.textBtn, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6,
            opacity: busy ? 0.5 : 1,
          }}>
          <RefreshCw size={14} style={busy ? { animation: 'voxSpin 1s linear infinite' } : undefined} />
          {busy ? 'Проверяю…' : 'Обновить'}
        </button>
        <style>{'@keyframes voxSpin { to { transform: rotate(360deg) } }'}</style>
      </div>

      {/* ── Это устройство ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray400 }}>Это устройство</div>
        <div style={{ fontFamily: 'monospace', fontSize: 15, color: colors.gray900 }}>{ownFingerprint}</div>
        <div style={{ fontFamily: FONT, fontSize: 12, color: colors.gray400, lineHeight: 1.45 }}>
          Восемь символов вашего ключа. Прочитайте их собеседнику вслух — если у него
          на этом экране те же, между вами никого нет.
        </div>
        <div style={{ fontFamily: FONT, fontSize: 12, color: isEvictable() ? colors.red : colors.gray400 }}>
          Ключи: {secureBackend() === 'ios-keychain' ? 'Keychain'
            : secureBackend() === 'android-encrypted-prefs' ? 'зашифрованное хранилище Android'
            : 'localStorage — браузер может их удалить, и устройство станет новым'}
        </div>
      </div>

      {/* ── Ключи собеседников ── */}
      <div style={{ borderTop: `1px solid ${colors.gray200}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray400 }}>
          Ключи собеседников, которым доверяет это устройство
        </div>

        {contacts.length === 0 && !busy && (
          <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray400 }}>Контактов пока нет</div>
        )}

        {contacts.map(({ user, devices, pinned }) => {
          // The server says how many devices exist; we say how many keys we
          // have pinned. More devices than keys means one of them has never
          // been confirmed — which is exactly when sending is blocked.
          // Three different states, and they used to be one alarming sentence.
          // A device this phone already has a session with is one it is
          // talking to right now: that session predates key checking, so
          // nothing is blocked — the key was simply never compared. A device
          // with no session and no pinned key is the one that actually stops
          // sending until somebody confirms it.
          const talking = devices.filter(d => d.hasSession).length;
          return (
            <div key={user.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 500, color: colors.gray900 }}>
                  {displayName(user)}
                </div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: colors.gray400 }}>
                  устройств: {devices.length}
                </div>
                {pinned.length > 0 && (
                  <button onClick={() => forget(user.id, displayName(user))}
                    style={{ ...S.textBtn, marginLeft: 'auto', color: colors.red, fontSize: 13 }}>
                    Сбросить доверие
                  </button>
                )}
              </div>

              {pinned.map((k) => (
                <div key={k.key} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                  {k.verified
                    ? <ShieldCheck size={14} color={colors.green} />
                    : <Shield size={14} color={colors.gray400} />}
                  <span style={{ fontFamily: 'monospace', fontSize: 13, color: colors.gray900 }}>
                    {shortFingerprint(k.key)}
                  </span>
                  <span style={{ fontFamily: FONT, fontSize: 12, color: colors.gray400 }}>
                    {k.verified ? 'сверен голосом' : 'запомнен'} · {new Date(k.firstSeen).toLocaleDateString('ru-RU')}
                    {k.label ? ` · ${k.label}` : ''}
                  </span>
                </div>
              ))}

              {devices.map(d => (
                <div key={d.deviceId} style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4 }}>
                  {d.hasSession
                    ? <ShieldCheck size={14} color={colors.gray400} />
                    : <ShieldAlert size={14} color={colors.red} />}
                  <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: colors.gray900 }}>
                    {d.deviceLabel || 'устройство'} · {String(d.deviceId).slice(0, 8)}
                  </span>
                  <span style={{ fontFamily: FONT, fontSize: 12, color: d.hasSession ? colors.gray400 : colors.red }}>
                    {d.hasSession ? 'разговор идёт' : 'новое — отправка встанет до подтверждения'}
                    {d.lastSeen ? ` · ${new Date(String(d.lastSeen).replace(' ', 'T') + 'Z')
                      .toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                </div>
              ))}

              {pinned.length === 0 && talking > 0 && (
                <div style={{ fontFamily: FONT, fontSize: 12.5, color: colors.gray400, paddingLeft: 4 }}>
                  Сверенных ключей нет: разговор идёт по сессии, созданной до того, как появилась
                  проверка ключей. Сверить можно голосом в звонке — тогда здесь появится галочка.
                </div>
              )}

              {pinned.length === 0 && talking === 0 && devices.length === 0 && (
                <div style={{ fontFamily: FONT, fontSize: 12.5, color: colors.gray400, paddingLeft: 4 }}>
                  Устройств пока нет — ключи запомнятся при первом сообщении или звонке
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        background: colors.gray50, borderRadius: radii.sm, padding: '10px 12px',
        fontFamily: FONT, fontSize: 12.5, color: colors.gray900, lineHeight: 1.45,
      }}>
        «Сбросить доверие» стирает то, что это устройство помнит о ключах человека.
        Следующий его ключ будет принят без вопросов — как при первом знакомстве.
        Это нужно, если он сменил телефон, и <b>не</b> нужно, если вы просто увидели
        предупреждение и хотите, чтобы оно исчезло.
      </div>

      {/* A button that does something invisible is a button people press twice
          and then stop trusting. */}
      <div style={{ fontFamily: FONT, fontSize: 12, color: error ? colors.red : colors.gray400 }}>
        {error || (refreshedAt
          ? `Проверено у сервера в ${refreshedAt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })} — устройства собеседников и ключи, которые помнит этот телефон`
          : 'Ещё не проверялось')}
      </div>

      {note && (
        <div style={{ fontFamily: FONT, fontSize: 13, color: colors.gray900 }}>{note}</div>
      )}
    </div>
  );
}
